import React from 'react'
import { useTranslation } from 'react-i18next'

import Typography from '@mui/material/Typography'
import Rating from '@mui/material/Rating'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'

import { Profile } from '../../types/profile'
import { get, post } from '../../utils/service'
import Alert from '../alert/alert'

interface ProfileRatingProps {
  profile: Profile
}

const ProfileRating = (props: ProfileRatingProps): JSX.Element => {
  const { t } = useTranslation()

  const [rating, setRating] = React.useState<number | null>(null)
  const [isSavingRating, setIsSavingRating] = React.useState<boolean>(false)
  const [hasRatingError, setHasRatingError] = React.useState<boolean>(false)
  const [isLoadingRating, setIsLoadingRating] = React.useState<boolean>(false)

  React.useEffect(() => {
    setIsLoadingRating(true)
    get<{ rating: number }>(`/profiles/${props.profile.id}/rating/`)
      .then((response) => {
        setIsLoadingRating(false)
        setRating(response.data?.rating || 0)
      })
      .catch(() => {
        setIsLoadingRating(false)
      })
  }, [props.profile])

  const rateProfile = (newRating: number): void => {
    setIsSavingRating(true)
    setHasRatingError(false)
    post<{ rating: number }, {}>(`/profiles/${props.profile.id}/rating/`, {
      rating: newRating,
    })
      .then(() => {
        setIsSavingRating(false)
      })
      .catch(() => {
        setIsSavingRating(false)
        setHasRatingError(true)
      })
  }

  return (
    <Stack>
      <Typography sx={{ mt: 2 }} variant="caption">
        {t('profilesview.rateprofile')}
      </Typography>
      {isLoadingRating && <CircularProgress />}
      {!isLoadingRating && (
        <Stack direction="row" spacing={2}>
          <Rating
            sx={{ color: 'primary.main', fontSize: { xs: '3rem', md: '1.9rem' } }}
            value={rating}
            onChange={(_event, newValue: number | null) => {
              if (newValue) {
                setRating(newValue)
                rateProfile(newValue)
              }
            }}
          />
          {isSavingRating && <CircularProgress size="1.5rem" />}
        </Stack>
      )}
      {hasRatingError && <Alert severity="error">{t('profilesview.setratingerror')}</Alert>}
    </Stack>
  )
}

export default ProfileRating
